// import APIs
import { UpdateClientAbsenceOptions } from 'RestAPIs/absence';

// import atoms
import { planResponsePlanningEntityIdentifiersAtom } from '../../atoms';

// import components
import { Box, Button, Collapse, DialogActions, Stack } from '@mui/material';
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker,
  ControlledToggleButtonGroup
} from 'components/shared';
import {
  GridCompact12,
  GridCompact4,
  GridCompact8,
  GridContainer,
  GridContainerMediumMaxWidth
} from 'components/shared/Grid/Grid.components';
import { showSuccessSnackbar } from 'components/shared/Snackbar';

// import external libraries
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

// import hooks
import { useCreateAbsence, useUpdateClientAbsence } from '../../mutations';
import { useGetClientAbsenceReasons } from '../../queries';

// import schema
import addAbsenceSchema from './add-absence-schema';

// import types
import { AddAbsenceFormProps, Form } from './AddAbsenceForm.types';

// import utils
import { refetchPlanResponsePlanningEntityIdentifiers } from '../../utils';

export default function AddAbsenceForm({
  appointmentId,
  appointmentStart,
  clientId,
  clientName,
  handleClose,
  clientAbsence
}: AddAbsenceFormProps) {
  const { t } = useTranslation('common');
  const { data: absenceReasons } = useGetClientAbsenceReasons();
  const { mutate: createClientAbsence } = useCreateAbsence();
  const { mutate: updateClientAbsence } = useUpdateClientAbsence();
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const schema = addAbsenceSchema();

  const isNewAppointmentAbsence = !clientAbsence && appointmentId;

  const defaultValues = clientAbsence
    ? {
        reasonId: clientAbsence.reasonId,
        description: clientAbsence.description,
        noEndDate:
          !clientAbsence.end ||
          clientAbsence.end.includes('9999') ||
          !dayjs(clientAbsence.end).isValid(),
        startDate: dayjs(clientAbsence.start),
        endDate: dayjs(clientAbsence.end),
        startTime: dayjs(clientAbsence.start),
        endTime: dayjs(clientAbsence.end)
      }
    : {
        description: '',
        startDate: dayjs(appointmentStart),
        endDate: dayjs(appointmentStart),
        startTime: dayjs(appointmentStart).startOf('day'),
        endTime: dayjs(appointmentStart).endOf('day'),
        noEndDate: !appointmentId,
        isAbsenceForAllAppointments: undefined,
        isNewAppointmentAbsence
      };

  const { control, handleSubmit, watch } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const { noEndDate, absenceAppointmentType } = watch();

  function onSubmit(data: Form) {
    const {
      reasonId,
      description,
      noEndDate,
      startDate,
      startTime,
      endDate,
      endTime
    } = data;

    const newStartDate = startDate
      .hour(startTime.hour())
      .minute(startTime.minute());
    const newEndDate = endDate?.hour(endTime.hour()).minute(endTime.minute());

    if (clientId !== undefined) {
      const payload = {
        clientId,
        reasonId,
        description,
        start: newStartDate.toDate(),
        end: noEndDate ? null : newEndDate?.toDate(),
        absentAppointments:
          isNewAppointmentAbsence && absenceAppointmentType !== 'ALL'
            ? [{ id: appointmentId, originalId: appointmentId }]
            : null
      };

      const onComplete = () => {
        showSuccessSnackbar();
        refetchPlanResponsePlanningEntityIdentifiers(
          setPlanResponsePlanningEntityIdentifiers
        );
        handleClose();
      };

      if (clientAbsence) {
        const editPayload = {
          ...payload,
          clientAbsenceId: clientAbsence.clientAbsenceId,
          absentAppointments: clientAbsence.absentAppointments
        } as UpdateClientAbsenceOptions;

        updateClientAbsence(editPayload, {
          onSuccess: onComplete
        });
      } else {
        createClientAbsence(payload, {
          onSuccess: onComplete
        });
      }
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <GridContainerMediumMaxWidth>
          <GridCompact12>
            <ControlledSelect
              name="reasonId"
              control={control}
              label={t('reasonAbsence')}
              options={
                absenceReasons?.map(({ id, name }) => ({
                  label: name,
                  value: id
                })) ?? []
              }
            />
          </GridCompact12>
          <GridCompact12>
            <ControlledTextField
              name="description"
              control={control}
              label={t('description')}
              fullWidth
              multiline
            />
          </GridCompact12>
          <GridCompact8 mt={1}>
            <ControlledDatePicker
              name="startDate"
              control={control}
              label={t('startDate')}
              fullWidth
            />
          </GridCompact8>
          <GridCompact4 mt={1}>
            <ControlledTimePicker
              name="startTime"
              control={control}
              label={t('startTime')}
              fullWidth
            />
          </GridCompact4>
          <GridCompact12>
            <ControlledCheckbox
              name="noEndDate"
              control={control}
              label={t('indefinite')}
            />
          </GridCompact12>
          <GridCompact12>
            <Collapse in={!noEndDate} sx={{ width: '100%' }}>
              <GridContainer>
                <GridCompact8>
                  <ControlledDatePicker
                    name="endDate"
                    control={control}
                    label={t('endDate')}
                    fullWidth
                  />
                </GridCompact8>
                <GridCompact4>
                  <ControlledTimePicker
                    name="endTime"
                    control={control}
                    label={t('endTime')}
                    fullWidth
                  />
                </GridCompact4>
              </GridContainer>
            </Collapse>
          </GridCompact12>
          {isNewAppointmentAbsence && (
            <GridCompact12>
              <ControlledToggleButtonGroup
                control={control}
                name="absenceAppointmentType"
                color="secondary"
                aria-label="alignment"
                label={t('activityPlanning.absenceAppointmentType')}
                inputToggleButtons={[
                  {
                    label: t('activityPlanning.thisAppointment'),
                    value: 'SINGLE',
                    selected: absenceAppointmentType === 'SINGLE'
                  },
                  {
                    label: t('activityPlanning.allAppointments'),
                    value: 'ALL',
                    selected: absenceAppointmentType === 'ALL'
                  }
                ]}
              />
            </GridCompact12>
          )}
        </GridContainerMediumMaxWidth>
        {!isNewAppointmentAbsence ||
          (isNewAppointmentAbsence && absenceAppointmentType !== undefined && (
            <Box>
              {absenceAppointmentType === 'ALL'
                ? t(
                    'activityPlanning.addAbsenceDialog.allAppointmentsHelperText',
                    {
                      clientName
                    }
                  )
                : t(
                    'activityPlanning.addAbsenceDialog.singleAppointmentHelperText',
                    {
                      clientName
                    }
                  )}
            </Box>
          ))}
      </Stack>
      <DialogActions sx={{ mt: 3 }}>
        <Button
          type="button"
          variant="text"
          color="primary"
          onClick={handleClose}
        >
          {t('cancel')}
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {t('save')}
        </Button>
      </DialogActions>
    </form>
  );
}
